import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import isEqual from 'lodash/isEqual';
import { Editable, Label } from '@adsk/bim360-matrix-react-components';
import { connect } from '../redux/connect';
import { updateIssueAction, trackEvent } from '../redux/actions';
import { currentUserRoleForAnalytics } from '../redux/selectors';
import { issueAttributesType } from '../constants/proptype-validation';
import {
  ISSUE_BE_ATTRIBUTE_NAMES,
  ISSUE_ATTRIBUTE_NAMES,
} from '../constants/issue-attribute-names';
import { fieldEdit } from '../utils/analytics-helpers';
import IssueInfoPopover from './IssueInfoPopover';
import { isAttributePermitted } from '../utils/issues-helpers';

export class PureIssueFieldInlineEdit extends PureComponent {
  constructor(props) {
    super(props);
    const { issue } = this.props;

    this.state = {
      value: this.getValue(issue),
      isUpdateFailed: false,
    };

    this.onUpdateSuccess = this.onUpdateSuccess.bind(this);
    this.onUpdateError = this.onUpdateError.bind(this);
  }

  componentWillMount() {
    const { issue } = this.props;

    this.updateState(issue);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!isEqual(this.props.issue, nextProps.issue)) {
      this.updateState(nextProps.issue);
    }
  }

  getValue(issue) {
    const { attributeName } = this.props;
    return issue[attributeName];
  }

  updateState(issue) {
    this.setState({
      value: this.props.getFieldValue(issue, this.props),
    });
  }

  onUpdateError(onUpdateFinish) {
    const { issue } = this.props;
    // Update state to original issue field value on update error
    this.updateState(issue);
    this.setState({ isUpdateFailed: true });
    onUpdateFinish && onUpdateFinish();
  }

  onUpdateSuccess(updatedIssue, onUpdateFinish) {
    const { issue, attributeName, trackEvent } = this.props;

    // Track fieldEdit analytics event, assignee edit has a separate event (`assigneeChange`)
    // which is triggered from the middleware so we ignore it here
    if (
      issue[attributeName] !== updatedIssue[attributeName] &&
      attributeName !== ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO
    ) {
      trackEvent(
        fieldEdit(
          attributeName,
          issue.status,
          this.props.currentUserRoleForAnalytics,
          this.state.isUpdateFailed
        )
      );
    }
    onUpdateFinish && onUpdateFinish();
  }

  updateIssueWrapper(closeEditMode) {
    return (newValue, onUpdateFinish) => {
      const { issue, getIssueAttributes, updateIssueAction } = this.props;
      updateIssueAction(issue, getIssueAttributes(newValue, this.props), {
        onError: () => this.onUpdateError(onUpdateFinish),
        onSuccess: (updatedIssue) => this.onUpdateSuccess(updatedIssue, onUpdateFinish),
      });

      this.setState({
        value: newValue,
      });

      // if onUpdateFinish we shouldn't close the inline edit since onUpdateFinish should handle it
      // example of usage is when showing a loader while updating and closing the inline edit only when update is finished
      !onUpdateFinish && closeEditMode();
    };
  }

  renderLabel() {
    const { label, container, infoMessage } = this.props;
    if (label || infoMessage) {
      return (
        <div className="IssueFieldInlineEdit__label-container">
          {label && <Label className="IssueFieldInlineEdit__label">{label}</Label>}
          {infoMessage && <IssueInfoPopover message={infoMessage} container={container} />}
        </div>
      );
    }
    return null;
  }

  render() {
    const {
      issue,
      attributeName,
      getDisplayValue,
      placeholder,
      className,
      children,
      disabled,
      styleType,
      linkify,
      multiline,
      horizontalAlign,
    } = this.props;
    const currentValue = this.state.value;
    const divClassNames = classnames(
      `IssueFieldInlineEdit IssueFieldInlineEdit--${styleType}`,
      { 'IssueFieldInlineEdit--horizontal': horizontalAlign },
      className
    );
    const editableClassNames = classnames('Editable', { 'Editable--with-prefix': !!currentValue });
    return (
      <div className={divClassNames}>
        {this.renderLabel()}
        <Editable
          className={editableClassNames}
          data-testid={this.props['data-testid']}
          value={getDisplayValue(currentValue)}
          placeholder={placeholder}
          readOnly={
            disabled || !isAttributePermitted(issue, ISSUE_BE_ATTRIBUTE_NAMES[attributeName])
          }
          linkify={linkify}
          multiline={multiline}
        >
          {(closeEditMode) =>
            children(currentValue, closeEditMode, this.updateIssueWrapper(closeEditMode))
          }
        </Editable>
      </div>
    );
  }
}

PureIssueFieldInlineEdit.displayName = 'IssueFieldInlineEdit';

PureIssueFieldInlineEdit.StyleTypes = {
  DEFAULT: 'default',
  SMALL: 'small',
};

PureIssueFieldInlineEdit.defaultProps = {
  styleType: PureIssueFieldInlineEdit.StyleTypes.DEFAULT,
  getDisplayValue: (v) => v,
  getFieldValue: (issue, props) => {
    const { attributeName } = props;
    return issue[attributeName];
  },
  getIssueAttributes: (value, props) => {
    return {
      [props.attributeName]: value,
    };
  },
};

PureIssueFieldInlineEdit.propTypes = {
  issue: issueAttributesType,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  attributeName: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  getDisplayValue: PropTypes.func.isRequired,
  getFieldValue: PropTypes.func,
  getIssueAttributes: PropTypes.func,
  children: PropTypes.func.isRequired,
  updateIssueAction: PropTypes.func.isRequired,
  trackEvent: PropTypes.func,
  infoMessage: PropTypes.string,
  container: PropTypes.object,
  styleType: PropTypes.oneOf(Object.values(PureIssueFieldInlineEdit.StyleTypes)),
  linkify: PropTypes.bool,
  multiline: PropTypes.bool,
  'data-testid': PropTypes.string,
  currentUserRoleForAnalytics: PropTypes.string.isRequired,
  horizontalAlign: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  currentUserRoleForAnalytics: currentUserRoleForAnalytics(state),
});

export default connect(mapStateToProps, {
  updateIssueAction,
  trackEvent,
})(PureIssueFieldInlineEdit);
